// app/routes/resources+/upload-media.tsx
import { parseFormData, type FileUpload } from '@mjackson/form-data-parser'
import { createClient } from '@supabase/supabase-js'
import { data } from 'react-router'
import { formatBytes } from '#app/components/ui/dropzone.tsx'
import { requireUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { type Route } from './+types/upload-media.ts'
import { type Media } from '#app/../prisma/generated/client'

const supabase = createClient(
	process.env.SUPABASE_URL!,
	process.env.SUPABASE_SERVICE_KEY!,
)

export const ALLOWED_FILE_TYPES = {
	IMAGES: {
		types: ['image/jpeg', 'image/png', 'image/webp'],
		wildcard: 'image/*',
		errorMessage: 'Only JPEG, PNG and WebP images are allowed.',
	},
	DOCUMENTS: {
		types: [
			'application/pdf',
			'application/msword',
			'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		],
		errorMessage: 'Only PDF and Word documents are allowed.',
	},
} as const

// Default size limits (can be overridden when needed)
export const DEFAULT_FILE_SIZES = {
	IMAGES: 1024 * 1024 * 10, // 10MB
	DOCUMENTS: 1024 * 1024 * 25, // 25MB
} as const

//  Get all allowed MIME types for given file types
export function getAllowedMimeTypes(
	fileTypes: Array<keyof typeof ALLOWED_FILE_TYPES>,
) {
	return fileTypes.flatMap((type) => {
		const config = ALLOWED_FILE_TYPES[type]
		return 'wildcard' in config ? [config.wildcard] : config.types
	})
}

// Get file config based on MIME type
function getFileConfigForMimeType(mimeType: string) {
	if (mimeType.startsWith('image/')) {
		return {
			errorMessage: ALLOWED_FILE_TYPES.IMAGES.errorMessage,
			maxSize: DEFAULT_FILE_SIZES.IMAGES,
		}
	}
	return {
		errorMessage: ALLOWED_FILE_TYPES.DOCUMENTS.errorMessage,
		maxSize: DEFAULT_FILE_SIZES.DOCUMENTS,
	}
}

export async function action({ request }: Route.ActionArgs) {
	const userId = await requireUserId(request)
	if (!userId) return data({ error: 'Unauthorized' }, { status: 401 })

	try {
		const clonedRequest = request.clone()
		const body = await clonedRequest.formData()
		const bucketName = body.get('bucketName') as string
		const path = body.get('path') as string
		const allowedMimeTypes =
			(body.get('allowedMimeTypes') as string)?.split(',') || []
		const maxSize = body.get('maxFileSize')
			? parseInt(body.get('maxFileSize') as string)
			: undefined

		// Compute RBAC access for media
		const userPerms = await prisma.user.findUnique({
			where: { id: userId },
			select: {
				roles: {
					select: {
						permissions: {
							where: { action: 'create', entity: 'media' },
							select: { access: true },
						},
					},
				},
			},
		})

		const accesses = userPerms?.roles.flatMap((r) =>
			r.permissions.map((p) => p.access),
		)
		const hasAny = accesses?.includes('any') ?? false

		// Validate requested path if not 'any'
		if (!hasAny && path) {
			const relativeRequested = path.includes('/')
				? path.split('/').slice(1).join('/')
				: path

			const businesses = await prisma.business.findMany({
				where: { ownerId: userId },
				select: { id: true },
			})
			const allowedBases = [userId, ...businesses.map((b) => b.id)]

			const isAllowed = allowedBases.some(
				(base) =>
					relativeRequested === base ||
					relativeRequested.startsWith(`${base}/`),
			)

			if (!isAllowed) {
				return data(
					{
						error: {
							message: 'You do not have permission to upload to this path',
							code: 403,
						},
					},
					{ status: 403 },
				)
			}
		}

		const uploadedFiles: Media[] = []

		const formData = await parseFormData(
			request,
			{ maxFileSize: maxSize ?? DEFAULT_FILE_SIZES.DOCUMENTS }, // Use provided size or largest default
			async (fileUpload: FileUpload) => {
				console.log('Processing file:', {
					name: fileUpload.name,
					type: fileUpload.type,
				})

				// Check if file type is allowed
				const isAllowedType = allowedMimeTypes.some((allowed) => {
					if (allowed.endsWith('/*')) {
						const type = allowed.replace('/*', '')
						return fileUpload.type.startsWith(type)
					}
					return allowed === fileUpload.type
				})

				if (!isAllowedType) {
					const fileConfig = getFileConfigForMimeType(fileUpload.type)
					throw new Error(
						JSON.stringify({
							fileName: fileUpload.name,
							code: 'InvalidMimeType',
							message: fileConfig.errorMessage,
						}),
					)
				}

				const { error } = await supabase.storage
					.from(bucketName)
					.upload(`${path}/${fileUpload.name}`, fileUpload.stream(), {
						contentType: fileUpload.type,
						upsert: body.get('upsert') === 'true',
						cacheControl: body.get('cacheControl') as string,
						duplex: 'half',
					})

				if (error) {
					throw new Error(
						JSON.stringify({
							fileName: fileUpload.name,
							code: error.message,
							message: getErrorMessage(
								error.message,
								maxSize ?? DEFAULT_FILE_SIZES.DOCUMENTS,
							),
							details: error.name,
						}),
					)
				}

				const {
					data: { publicUrl },
				} = supabase.storage
					.from(bucketName)
					.getPublicUrl(`${path}/${fileUpload.name}`)

				// Only remove first element if path contains multiple segments
				const relativePath = path.includes('/')
					? path.split('/').slice(1).join('/')
					: path

				// Create Media record in DB
				const media = await prisma.media.create({
					data: {
						name: fileUpload.name,
						bucket: bucketName,
						path: relativePath,
						mimeType: fileUpload.type,
						url: publicUrl,
					},
				})

				uploadedFiles.push(media)
				return
			},
		)

		return data({
			success: true,
			files: formData.getAll('files'),
			media: uploadedFiles,
		})
	} catch (error) {
		const errorMessage =
			error instanceof Error ? error.message : 'Upload failed'
		return data(
			{
				error: {
					message: errorMessage,
					code: 400,
					details: 'Validation error',
				},
			},
			{ status: 400 },
		)
	}
}

function getErrorMessage(errorCode: string, maxSize: number) {
	switch (errorCode) {
		case 'ResourceAlreadyExists':
			return 'File already exists. Enable upsert to overwrite.'
		case 'EntityTooLarge':
			return `File exceeds size limit of ${formatBytes(maxSize)}`
		case 'InvalidMimeType':
			return 'Invalid file type'
		case 'AccessDenied':
			return 'You do not have permission to upload to this bucket'
		case 'NoSuchBucket':
			return 'The specified storage bucket does not exist'
		default:
			return errorCode
	}
}
